import React, { useCallback, useEffect, useState } from 'react';

import { removeSnackbar } from 'flow/actions/app-actions';
import useAppContext from 'flow/hooks/useAppContext';
import { getSnackbarData, hasSnackbarsWaiting } from 'flow/selectors/selectors';
import Icon from './ui-components/Icon';
import IconButton from './ui-components/IconButton';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

const Snackbar = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const snackbarData = useAppSelector(getSnackbarData);
    const hasSnackbarsWaitingValue = useAppSelector(hasSnackbarsWaiting);

    const snackbarKey = snackbarData?.get('key') || '';
    const hasCloseButton = snackbarData?.get('hasCloseButton') || false;
    const autoHideDurationValue = hasCloseButton ? null : 2500;

    const [open, setOpen] = useState(false);

    const handleExited = useCallback(() => {
        dispatch(removeSnackbar());
    }, [dispatch]);

    useEffect(() => {
        if (hasSnackbarsWaitingValue) {
            setOpen(false);
        }
    }, [hasSnackbarsWaitingValue]);

    useEffect(() => {
        if (snackbarKey) {
            setOpen(true);
        }
    }, [snackbarKey]);

    useEffect(() => {
        if (open && autoHideDurationValue) {
            const timer = setTimeout(() => {
                setOpen(false);
            }, autoHideDurationValue);

            return () => clearTimeout(timer);
        }
    }, [open, autoHideDurationValue]);

    useEffect(() => {
        if (!open && snackbarKey) {
            const timer = setTimeout(() => {
                handleExited();
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [open, snackbarKey, handleExited]);

    if (!open || !snackbarKey) {
        return null;
    }

    const handleClose = (
        _event?: React.SyntheticEvent | Event,
        reason?: string,
    ) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const i18nextMessage = snackbarData?.get('i18nextMessage') || '';
    const i18nextMessageProps = snackbarData?.get('i18nextMessageProps') || {};

    const snackbarMessage =
        snackbarData?.get('message') ||
        i18next.t(i18nextMessage, i18nextMessageProps);

    return (
        <div
            className="animate-in slide-in-from-bottom-2 fade-in fixed bottom-0 left-1/2 z-50 mb-10 flex max-w-xl min-w-72 -translate-x-1/2 transform items-center gap-3 rounded-sm bg-gray-800 px-4 py-3 text-white shadow-lg duration-300"
            role="alert"
        >
            <span className="flex-1 text-sm">{snackbarMessage}</span>

            {hasCloseButton && (
                <div className="ms-auto flex items-center">
                    <IconButton
                        aria-label="Close"
                        className="text-white hover:text-gray-300"
                        onClick={handleClose}
                    >
                        <Icon iconKey="close" size="1rem" />
                    </IconButton>
                </div>
            )}
        </div>
    );
};

export default Snackbar;
